import {
  ComponentPropsWithoutRef,
  forwardRef,
  Children,
  isValidElement,
  cloneElement,
  CSSProperties,
  ReactElement,
} from "react";
import cx from "classnames";
import styles from "./AvatarGroup.module.scss";
import avatarBaseStyles from "./AvatarBase.module.scss";
import { LayoutUtilProps } from "../../types/props";
import { useLayoutPropsUtil } from "../../hooks";
import { AvatarBase } from "./AvatarBase";

/**
 * Props for the AvatarGroup component
 * @extends LayoutUtilProps
 * @extends ComponentPropsWithoutRef<"span">
 */
export type AvatarGroupProps = LayoutUtilProps &
  ComponentPropsWithoutRef<"span"> & {
    /**
     * Max child to show before truncating
     */
    max?: number;
  };

/**
 * AvatarGroup component for displaying multiple avatars in a group.
 *
 * Features:
 * - Displays multiple avatars in a stacked layout
 * - Handles overflow with a count indicator
 * - Maintains consistent sizing across all avatars
 * - Supports custom styling and layout
 * - Automatically adjusts z-index for proper stacking
 * - Fully accessible with screen reader support
 *
 * @example
 * <Avatar.Group max={3}>
 *   <Avatar name="John Doe" />
 *   <Avatar name="Jane Smith" />
 *   <Avatar name="Bob Johnson" />
 *   <Avatar name="Alice Brown" />
 * </Avatar.Group>
 */
export const AvatarGroup = forwardRef<HTMLSpanElement, AvatarGroupProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const { className, max, style, children, ...rest } = componentProps;
    const childrenArr = Children.toArray(children);
    const inferredSize =
      (isValidElement(childrenArr[0]) && childrenArr[0].props.size) ?? "medium";
    const isOverflowing = max ? max < childrenArr.length : false;

    const overflowCount = max && childrenArr.length - max;

    const avatarGroupClassNames = cx(styles["avatar-group"], className, {
      [styles[`${inferredSize}`]]: inferredSize,
    });

    const avatarBaseOverflowClassNames = cx(
      avatarBaseStyles["avatar-base-overflow"],
      {
        [avatarBaseStyles["overflow-ten-plus"]]:
          overflowCount && overflowCount > 9,
      },
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };
    return (
      <span
        ref={ref}
        className={avatarGroupClassNames}
        style={styleCombined}
        data-anv="avatar-group"
        {...rest}
      >
        {childrenArr.map((child, i) => {
          let childEl = child;
          if (isValidElement(child)) {
            const nIndex = childrenArr.length - i;
            const nStyle: CSSProperties = {
              ...child.props.style,
              zIndex: nIndex,
            };
            childEl = cloneElement(child as ReactElement, {
              style: nStyle,
            });
          }

          if (!max) return childEl;
          if (i < max) return childEl;
          return null;
        })}
        {isOverflowing && max && (
          <AvatarBase
            className={avatarBaseOverflowClassNames}
            name={`+${overflowCount}`}
            initials={`+${overflowCount}`}
            size={inferredSize}
          />
        )}
      </span>
    );
  },
);

AvatarGroup.displayName = "AvatarGroup";
